<template>
  <div class="search-box">
    <a-radio-group v-model="mode" size="small" class="mode-toggle" @change="onModeChange">
      <a-radio-button value="goods">商品</a-radio-button>
      <a-radio-button value="member">会员</a-radio-button>
    </a-radio-group>
    <div class="input-wrap">
      <a-input ref="input" v-model="keyword" class="search-input"
               :placeholder="placeholder" allowClear
               @input="onInput" @keydown="onKeydown" @focus="focused = true" @blur="onBlur" />
      <div v-if="focused && candidates.length" class="dropdown">
        <div v-for="(c, i) in candidates" :key="c.key"
             class="drop-item" :class="{ active: i === highlight }"
             @mousedown.prevent="pick(i)" @mouseenter="highlight = i">
          <span class="drop-name" v-html="c.label"></span>
          <span class="drop-sub">{{ c.sub }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { cashierGoods, cashierMemberSearch } from '@/api/plugin/member'

  /**
   * 搜索框（B8）：商品/会员双模式，防抖 300ms 联想下拉，↑↓ 选择回车入车/定位会员；
   * 唯一条码回车直入保留；商城订单码（JSHOR|MORDER|）透传 scan-order 事件
   */
  export default {
    name: 'SearchBox',
    props: {
      levelId: { type: [Number, String], default: null }
    },
    data() {
      return {
        mode: 'goods',
        keyword: '',
        candidates: [],
        highlight: -1,
        focused: false,
        timer: null
      }
    },
    computed: {
      placeholder() {
        return this.mode === 'goods'
          ? '扫条码入车 / 搜索商品（名称/助记码/条码）'
          : '扫会员码 / 输入手机号、姓名、卡号定位会员'
      }
    },
    methods: {
      focus() {
        this.$nextTick(() => {
          if (this.$refs.input) this.$refs.input.focus()
        })
      },
      onModeChange() {
        this.keyword = ''
        this.candidates = []
        this.focus()
      },
      onInput() {
        if (this.timer) clearTimeout(this.timer)
        this.timer = setTimeout(this.search, 300)
      },
      search() {
        const kw = (this.keyword || '').trim()
        if (!kw) {
          this.candidates = []
          this.highlight = -1
          return
        }
        if (this.mode === 'goods') {
          cashierGoods({ search: kw, levelId: this.levelId || undefined, currentPage: 1, pageSize: 10 })
            .then(res => {
              if (res && res.code === 200) {
                const list = []
                ;(res.data.rows || []).forEach(goods => {
                  ;(goods.units || []).forEach(unit => {
                    list.push({
                      key: 'u' + unit.extendId,
                      type: 'goods',
                      goods,
                      unit,
                      label: this.highlightText(goods.name, kw),
                      sub: `${unit.unit} · ${unit.barCode || '无条码'} · ¥${Number(unit.price).toFixed(2)}`
                    })
                  })
                })
                this.candidates = list
                this.highlight = list.length ? 0 : -1
              }
            })
        } else {
          cashierMemberSearch(kw).then(res => {
            if (res && res.code === 200) {
              this.candidates = (res.data || []).map(m => ({
                key: 'm' + m.memberId,
                type: 'member',
                member: m,
                label: this.highlightText(m.contacts || m.cardNo, kw),
                sub: `${m.telephone || '无手机号'} · ${m.levelName || '普通会员'}`
              }))
              this.highlight = this.candidates.length ? 0 : -1
            }
          })
        }
      },
      highlightText(text, kw) {
        const s = String(text || '')
        const idx = s.indexOf(kw)
        if (idx < 0) return s
        return `${s.slice(0, idx)}<b class="hit">${kw}</b>${s.slice(idx + kw.length)}`
      },
      onKeydown(e) {
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          if (this.candidates.length) {
            this.highlight = (this.highlight + 1) % this.candidates.length
          }
        } else if (e.key === 'ArrowUp') {
          e.preventDefault()
          if (this.candidates.length) {
            this.highlight = (this.highlight - 1 + this.candidates.length) % this.candidates.length
          }
        } else if (e.key === 'Enter') {
          e.preventDefault()
          this.enter()
        }
      },
      enter() {
        const kw = (this.keyword || '').trim()
        if (!kw) return
        if (kw.startsWith('JSHOR|MORDER|')) {
          this.$emit('scan-order', kw)
          this.reset()
          return
        }
        if (!this.candidates.length) {
          if (this.mode === 'member') {
            this.$message.warning('未找到会员')
          }
          return
        }
        // 商品模式：唯一条码精确匹配直接入车（扫码枪流）
        if (this.mode === 'goods') {
          const hits = this.candidates.filter(c => c.unit && c.unit.barCode === kw)
          if (hits.length === 1) {
            this.emitPick(hits[0])
            return
          }
          if (hits.length > 1) {
            this.$message.warning('条码匹配到多个商品，请手动选择')
            return
          }
        }
        this.emitPick(this.candidates[this.highlight >= 0 ? this.highlight : 0])
      },
      pick(i) {
        if (this.candidates[i]) {
          this.emitPick(this.candidates[i])
        }
      },
      emitPick(candidate) {
        if (candidate.type === 'goods') {
          this.$emit('pick-goods', { goods: candidate.goods, unit: candidate.unit })
        } else {
          this.$emit('pick-member', candidate.member)
        }
        this.reset()
      },
      reset() {
        this.keyword = ''
        this.candidates = []
        this.highlight = -1
        this.focus()
      },
      onBlur() {
        setTimeout(() => {
          this.focused = false
        }, 150)
      }
    }
  }
</script>

<style scoped>
  .search-box {
    display: flex;
    gap: 8px;
    margin-bottom: 4px;
  }
  .mode-toggle {
    flex-shrink: 0;
  }
  .input-wrap {
    flex: 1;
    position: relative;
  }
  .dropdown {
    position: absolute;
    top: 34px;
    left: 0;
    right: 0;
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, .12);
    z-index: 100;
    max-height: 320px;
    overflow-y: auto;
  }
  .drop-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 7px 12px;
    cursor: pointer;
    font-size: 13px;
  }
  .drop-item.active {
    background: #e6f4ff;
  }
  .drop-name {
    color: #333;
  }
  .drop-name >>> .hit {
    color: #e4393c;
  }
  .drop-sub {
    color: #999;
    font-size: 12px;
  }
</style>
